import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { BreadCrumb, BreadCrumbs } from '../../components/BreadCrumbs';
import { CardBody } from '../../components/CardBody';
import { ProductPriceSummary } from '../../components/ProductPriceSummary';
import { environmentContextState, shopifyState, toViewPriceFnState } from '../../recoil/atoms';
import { ImageLoader } from '../../product_detail/components/ImageLoader';
import { CardHeader } from '../../components/CardHeader';
import { ProductControls } from '../../components/ProductControls';
import { SelectionType } from '../../enums/SelectionType.enum';
import { productsState } from '../../recoil/selectors';
import { ProductPerks } from '../../components/ProductPerks';
import { ModelName, ModelPhoto, useProductAssets } from '../../hooks/useProductAssets';
import { isDefined, uniqueFilterByProperty } from '../../sizing-tool/utils/array-helper';
import { sizeImage } from '../../util';
import { shortSizeToLongSize } from '../../product_detail/components/size.enum';
import { CollapsePanelMobile } from '../../components/CollapsePanelMobile';
import { DivButton } from '../../root/components/DivButton';
import { ProductReviewsMobile } from '../../components/ProductReviewsMobile';
import { ScrollableContainer } from '../../collection/components/ScrollableContainer';
import { ProductSelector } from '../../collection/components/ProductSelector';
import { CollectionCards, sortByPopularity, sortByProductFirst } from '../../collection/components/CollectionCards';
import { CompleteTheLook } from '../../components/CompleteTheLook';
import { CardFooter } from '../../components/CardFooter';
import { useProductSelection } from '../../hooks/useProductSelection';
import { WistiaPanel } from '../../components/WistiaPanel';
import { CardTitle } from '../../components/CardTitle';
import { DiscoverHoneylove } from '../../sections/DiscoverHoneylove';
import { WorldsBestShapingPanel } from '../../components/WorldsBestShapingPanel';
import { ProductHandle } from '../../enums/ProductHandle.enum';
import classNames from 'classnames';
import { Button } from '../../root/components/buttons/Button';
import { freeShippingThresholdInDollars } from '../../discounts';
import { WorldsBestTitle } from '../../components/WorldsBestTitle';
import styles from './ProductDetailPageMobile.module.scss';

export const imageStyle: CSSProperties = {
  width: '100%',
  margin: '2px 0',
};

interface Props {
  breadCrumbs?: BreadCrumb[];
}

// eslint-disable-next-line max-lines-per-function
export const ProductDetailPageMobile: React.FC<Props> = ({ breadCrumbs }) => {
  const { product, color, variant } = useProductSelection(SelectionType.ProductSection, '0');

  const reviewsRef = useRef<HTMLDivElement>(null);
  const productAssets = useProductAssets(product);
  const [activeModel, setActiveModel] = useState<ModelName>(productAssets.modelPhotos?.[0]?.modelName);
  const shopify = useRecoilValue(shopifyState);
  const allProducts = useRecoilValue(productsState);
  const [showAllTopPhotos, setShowAllTopPhotos] = useState<boolean>(false);
  const [modelPhotos, setModelPhotos] = useState<ModelPhoto[]>([]);
  const toViewPriceFn = useRecoilValue(toViewPriceFnState);
  const { warehouseSaleSKUs, warehouseSaleEnabled } = useRecoilValue(environmentContextState);

  const isGiftCard = product.handle === ProductHandle.EGiftCard;

  const otherProducts = useMemo(() => {
    return Object.values(allProducts)
      .filter((p) => !p.hiddenInMenus)
      .sort(sortByPopularity)
      .sort(sortByProductFirst(product));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const customerInUsa = shopify?.currency.active === 'USD';
  const showFreeShipping = customerInUsa && product.handle !== ProductHandle.EGiftCard;

  useEffect(() => {
    setActiveModel(productAssets.modelPhotos?.[0]?.modelName);
    setShowAllTopPhotos(false);
  }, [productAssets]);

  useEffect(() => {
    const photos = productAssets.modelPhotos.filter((p) => p.color === color && p.modelName === activeModel);
    if (photos.length > 0) {
      setModelPhotos(photos);
    }
  }, [color, productAssets, activeModel]);

  const possibleModels = useMemo(() => {
    return productAssets.modelPhotos.filter((p) => p.color === color).filter(uniqueFilterByProperty((model) => model.modelName));
  }, [color, productAssets]);

  useEffect(() => {
    setActiveModel(possibleModels[0]?.modelName);
  }, [possibleModels]);

  const wistiaVideoId = productAssets.videos[0]?.wistiaId;

  const topPhotos = product.handle === ProductHandle.EGiftCard ? productAssets.renderPhotos : [...modelPhotos];

  const fabricPhotos = useMemo(() => {
    const photos = productAssets.fabricPhotos.filter((p) => p.color === color);
    return photos.length > 0 ? photos : [productAssets.fabricPhotos[0]].filter(isDefined);
  }, [color, productAssets]);

  const zoomInPhotos = useMemo(() => {
    const photos = productAssets.zoomInPhotos.filter((p) => p.color === color);
    return photos.length > 0 ? photos : [productAssets.zoomInPhotos[0]].filter(isDefined);
  }, [color, productAssets]);

  const toggleStyle = { justifyContent: 'start', alignItems: 'left', padding: '10px 20px 8px 20px' };

  const isWarehouseSaleVariant = !!product.variants.find((v) => warehouseSaleEnabled && warehouseSaleSKUs.includes(v.sku) && v.color === color);
  const isNoReturns = !product.isReturnable || isWarehouseSaleVariant;

  const isEmbedded = !breadCrumbs;

  return (
    <>
      <CardBody>
        {!isEmbedded && (
          <ScrollableContainer style={{ borderBottom: '1px solid #e5e5e5', paddingBottom: 5 }}>
            {otherProducts.map((p, i) => (
              <ProductSelector
                key={`prod-${i}`}
                product={p}
                selectionType={SelectionType.ProductSection}
                selectionKey="0"
                style={{ padding: '0 5px', lineHeight: '16px' }}
                active={p.id === product.id}
              />
            ))}
          </ScrollableContainer>
        )}
        {breadCrumbs && <BreadCrumbs items={breadCrumbs} style={{ borderBottom: 'none', marginTop: 5 }} />}
        <ProductPriceSummary
          product={product}
          variant={variant}
          reviewsSectionRef={reviewsRef}
          style={{ margin: `${isEmbedded ? '20px' : 0} 20px 0 20px` }}
          color={color}
        />
        <ProductPerks product={product} style={{ margin: '10px 20px' }} />
        {topPhotos[0] ? (
          <div className={topPhotos.length === 1 ? styles.imageBoxSingle : styles.imageBox}>
            <ImageLoader key={topPhotos[0].src} style={imageStyle} className={styles.grid} alt={`${product.title}`} src={sizeImage(topPhotos[0].src, '800x')} />
          </div>
        ) : (
          <></>
        )}
        <ProductControls selectionType={SelectionType.ProductSection} selectionKey="0" />
        <CollapsePanelMobile type="internal-state" title={`${!isGiftCard ? 'GARMENT ' : ''}DESCRIPTION`} initialState={'active'} toggleStyle={toggleStyle}>
          <div className={styles.panelBody}>
            <span className={styles.panelText}>{product.coreFeatures}</span>
          </div>
        </CollapsePanelMobile>
        {showFreeShipping && (
          <CollapsePanelMobile type="internal-state" title={`FREE SHIPPING${isNoReturns ? '' : ' & FREE RETURNS'}`} toggleStyle={toggleStyle}>
            <div className={styles.panelBody}>
              <span className={styles.panelText}>
                <div>&#8226;&nbsp;Free shipping over {`${toViewPriceFn(freeShippingThresholdInDollars, 0)}`}</div>
                {!isNoReturns && <div>&#8226;&nbsp;Free 30-day returns and exchanges</div>}
              </span>
            </div>
          </CollapsePanelMobile>
        )}
        {product.fitRecommendation && (
          <CollapsePanelMobile type="internal-state" title="FIT RECOMMENDATIONS" toggleStyle={toggleStyle}>
            <div className={styles.panelBody}>
              <span className={styles.panelText}>
                <div className={styles.panelSubTitle}>Fit Recommendation</div>
                <div>{product.fitRecommendation}</div>
              </span>
            </div>
          </CollapsePanelMobile>
        )}
        {product.fabricDetails?.length > 0 && (
          <CollapsePanelMobile type="internal-state" title="FABRIC DETAILS" style={{ borderBottom: '1px solid black' }} toggleStyle={toggleStyle}>
            <div className={styles.panelBody}>
              <span className={styles.panelText}>
                {product.fabricDetails.map((fd, i) => {
                  return <div key={i}>&#8226;&nbsp;{fd}</div>;
                })}
              </span>
            </div>
          </CollapsePanelMobile>
        )}
        {possibleModels.length >= 2 && (
          <div className={styles.modelNameContainer}>
            <DivButton
              onClick={() => setActiveModel(possibleModels[0].modelName)}
              label={`View image with ${possibleModels[0].modelName} in ${possibleModels[0].size}`}
            >
              <span className={classNames({ [styles.activeModel]: possibleModels[0].modelName === activeModel })}>{`${
                possibleModels[0].modelName
              }, ${shortSizeToLongSize(possibleModels[0].size)}`}</span>
            </DivButton>
            <DivButton
              onClick={() => setActiveModel(possibleModels[1].modelName)}
              label={`View image with ${possibleModels[1].modelName} in ${possibleModels[1].size}`}
            >
              <span className={classNames({ [styles.activeModel]: possibleModels[1].modelName === activeModel })}>{`${
                possibleModels[1].modelName
              }, ${shortSizeToLongSize(possibleModels[1].size)}`}</span>
            </DivButton>
          </div>
        )}
        {topPhotos.map((p, i) => {
          const showPhoto = i !== 0 && i <= 2;
          return showPhoto ? (
            <div className={styles.imageBox} key={p.src}>
              <ImageLoader style={imageStyle} alt={`${product.title} ${i}`} src={sizeImage(p.src, '800x')} />
            </div>
          ) : (
            <React.Fragment key={p.src}></React.Fragment>
          );
        })}
        {productAssets.productFeaturePhotos.map((p, i) => (
          <div className={styles.imageBox} key={p.src}>
            <ImageLoader style={imageStyle} alt={`${product.title} Feature ${i}`} src={sizeImage(p.src, '800x')} />
          </div>
        ))}
        {!isEmbedded && wistiaVideoId && <WistiaPanel videoId={wistiaVideoId} responsivePadding={{ padding: '145.0% 0 0 0' }} />}
        {fabricPhotos.map((p, i) => (
          <div className={styles.imageBox} key={p.src}>
            <ImageLoader
              style={{ border: '10px solid white', boxSizing: 'border-box', ...imageStyle }}
              alt={`${product.title} Feature ${i}`}
              src={sizeImage(p.src, '800x')}
            />
          </div>
        ))}
        {zoomInPhotos.map((p, i) => (
          <div className={styles.imageBox} key={p.src}>
            <ImageLoader style={imageStyle} alt={`${product.title} Feature ${i}`} src={sizeImage(p.src, '800x')} />
          </div>
        ))}
        {!showAllTopPhotos && topPhotos.length > 3 && <Button size="medium" onClick={() => setShowAllTopPhotos(true)} text="Show More" fullWidth={true} />}
        {showAllTopPhotos &&
          topPhotos.map((p, i) => {
            return i > 2 ? (
              <div className={styles.imageBox} key={p.src}>
                <ImageLoader style={imageStyle} alt={`${product.title} ${i}`} src={sizeImage(p.src, '800x')} />
              </div>
            ) : (
              <React.Fragment key={p.src}></React.Fragment>
            );
          })}
      </CardBody>
      <CardFooter />
      <CompleteTheLook product={product} selectionType={SelectionType.ProductSection} Wrapper={CompleteTheLookWrapper} />
      {!isGiftCard && (
        <>
          <CardHeader />
          <ProductReviewsMobile ref={reviewsRef} product={product} />
        </>
      )}
      <CardHeader>
        <CardTitle text="Featured" />
      </CardHeader>
      <CollectionCards hideHeader={true} />
      <CardHeader>
        <WorldsBestTitle />
      </CardHeader>
      <CardBody>
        <WorldsBestShapingPanel />
      </CardBody>
      <CardFooter />
      <CardHeader>
        <CardTitle text="Discover Honeylove" />
      </CardHeader>
      <CardBody>
        <DiscoverHoneylove />
      </CardBody>
    </>
  );
};

const CompleteTheLookWrapper: React.FC = ({ children }) => (
  <>
    <CardHeader />
    <CardBody style={{ paddingTop: 40 }}>{children}</CardBody>
    <CardFooter inlineStyles={{ paddingBottom: 40 }} />
  </>
);

export default ProductDetailPageMobile;
